"use client";

/**
 * An empty search names the filter to drop (owner's pick, 2026-10-29):
 * each applied filter is counted with itself removed (the list's own
 * count, page_size=1), and the three that would bring back the most
 * listings become buttons, "«Çıxarışlı» olmadan: 12 elan". The counts run
 * only when the result is empty.
 */
import { useEffect, useMemo, useState } from "react";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { useLocale, useT } from "@/lib/locale-context";
import { apiParamsFromSearch } from "@/lib/listing-search-params";
import { cachedFilterNames, loadFilterNames, type FilterNames } from "@/lib/filter-names";
import { appliedChips, type AppliedChip } from "./AppliedFilterChips";

const MAX_TRIES = 8;

function without(sp: ReadonlyURLSearchParams, remove: AppliedChip["remove"]): URLSearchParams {
  const next = new URLSearchParams(sp.toString());
  for (const [k, v] of Object.entries(remove)) {
    next.delete(k);
    if (Array.isArray(v)) v.forEach((x) => next.append(k, x));
    else if (v) next.set(k, v);
  }
  next.delete("page");
  return next;
}

function apiQuery(sp: URLSearchParams): string {
  const qp = new URLSearchParams();
  for (const [k, v] of Object.entries(apiParamsFromSearch(sp as unknown as ReadonlyURLSearchParams))) {
    if (Array.isArray(v)) v.forEach((x) => { if (x) qp.append(k, x); });
    else if (v) qp.set(k, String(v));
  }
  qp.set("page_size", "1");
  return qp.toString();
}

export default function ZeroResultsHint({ searchParams, onRemove }: {
  searchParams: ReadonlyURLSearchParams;
  onRemove: (updates: AppliedChip["remove"]) => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  const [names, setNames] = useState<FilterNames | null>(() => cachedFilterNames());
  const needsNames = ["location_ids", "complex_slug", "developer", "tags"].some((k) => searchParams.getAll(k).length > 0);
  useEffect(() => { if (needsNames && !names) loadFilterNames().then(setNames).catch(() => {}); }, [needsNames, names]);
  const key = searchParams.toString();
  const chips = useMemo(
    () => appliedChips(searchParams, t, names, locale).slice(0, MAX_TRIES),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, names, locale, t],
  );
  const [counts, setCounts] = useState<{ chip: AppliedChip; n: number }[] | null>(null);
  const chipsKey = chips.map((c) => c.id).join("|");
  useEffect(() => {
    setCounts(null);
    if (!chips.length) return;
    const ctrl = new AbortController();
    Promise.all(chips.map((chip) =>
      fetch(`/api/listings?${apiQuery(without(searchParams, chip.remove))}`, { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => ({ chip, n: typeof d?.total === "number" ? d.total : 0 }))
        .catch(() => ({ chip, n: 0 })),
    )).then((all) => {
      if (!ctrl.signal.aborted) setCounts(all.filter((x) => x.n > 0).sort((a, b) => b.n - a.n).slice(0, 3));
    });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, chipsKey]);

  if (!counts?.length) return null;
  const fmt = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return (
    <div className="mt-4 flex flex-col items-center gap-2">
      <p className="text-sm text-gray-600">{t("listings.empty.drop_hint")}</p>
      <div className="flex flex-wrap justify-center gap-2">
        {counts.map(({ chip, n }) => (
          <button
            key={chip.id}
            type="button"
            onClick={() => onRemove(chip.remove)}
            className="inline-flex h-9 items-center rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium tabular-nums text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900"
          >
            {t("listings.empty.without").replace("{label}", chip.label).replace("{n}", fmt(n))}
          </button>
        ))}
      </div>
    </div>
  );
}
